import Cookie from "js-cookie";
import { setCookie } from "~/helpers/cookie";
import { NextRouter } from "next/router";
import { Dispatch } from "redux";

import {
  ActionTypes,
  Address,
  AddressAction,
  AddressHistoryAction,
  Eligibility,
  EligibilityAction,
  Promotion,
  PromotionsAction,
  Redirect,
  ResetAddressRouteAction,
  ServiceabilityResponse,
  SetTechAvailableAction,
  SetContractTypeAction,
  SetIsFutureFiberEligibleAction,
  SetVoiceEligibleAction,
  UnservReasonAction,
  UnservRedirectAction,
  UpdateAddressAction,
} from "~/types/address";
import { ReduxState } from "~/types/redux";

import { setNewRelicCustomAttribute } from "~/helpers/newrelic";
import safeRedirect from "~/helpers/safeRedirect";

import { setCallInNumber, setPageLoading } from "./common";
import { setNotifications } from "./global";
import { getProducts, setOfferType } from "./products";
import { formatPhone } from "~/helpers/phone";

export const setAddress = (key: string, address: Address): AddressAction => {
  return { type: ActionTypes.SET_ADDRESS, key, address };
};

export const updateAddress = (key: string, address: Address): UpdateAddressAction => {
  return { type: ActionTypes.UPDATE_ADDRESS, key, address };
};

export const setEligibility = (eligibility: Eligibility): EligibilityAction => {
  return { type: ActionTypes.SET_ELIGIBILITY, eligibility };
};

export const setPromotions = (promotions: Promotion[]): PromotionsAction => {
  return { type: ActionTypes.SET_PROMOTIONS, promotions };
};

export const resetAddressRoute = (): ResetAddressRouteAction => {
  return { type: ActionTypes.RESET_ADDRESS_ROUTE };
};

export const addAddressHistory = (addressKey: string): AddressHistoryAction => {
  return { type: ActionTypes.ADDRESS_HISTORY, addressKey };
};

export const setUnservReason = (unservReason: string): UnservReasonAction => {
  return { type: ActionTypes.SET_UNSERV_REASON, unservReason };
};

export const setUnservRedirect = (unservRedirect: Redirect): UnservRedirectAction => {
  return { type: ActionTypes.SET_UNSERV_REDIRECT, unservRedirect };
};

export const setVoiceEligible = (voiceEligible: boolean): SetVoiceEligibleAction => {
  return { type: ActionTypes.SET_VOICE_ELIGIBLE, voiceEligible };
};

export const setTechAvailable = (techAvailable: string): SetTechAvailableAction => {
  return { type: ActionTypes.SET_TECH_AVAILABLE, techAvailable };
};

export const setContractType = (contractType: string): SetContractTypeAction => {
  return { type: ActionTypes.SET_CONTRACT_TYPE, contractType };
};

export const setIsFutureFiberEligible = (isFutureFiberEligible: boolean): SetIsFutureFiberEligibleAction => {
  return { type: ActionTypes.SET_IS_FUTURE_FIBER_ELIGIBLE, isFutureFiberEligible };
};

const handleDMAData = (dispatch: Dispatch, response: ServiceabilityResponse) => {
  const { designatedMarketArea, designatedMarketAreaDescription } = response;
  if (designatedMarketArea) {
    dispatch(updateAddress("install", { dma: designatedMarketArea }));
    setCookie("dma", designatedMarketArea, { otGroup: "C0003" });
  }
  if (designatedMarketAreaDescription) {
    dispatch(updateAddress("install", { dmaName: designatedMarketAreaDescription }));
    setCookie("dmaName", designatedMarketAreaDescription, { otGroup: "C0003" });
  }
};

export const clearServiceabilityResults = () => {
  return (dispatch: Dispatch): void => {
    dispatch(resetAddressRoute());
  };
};

export const setServiceabilityResults = ({
  response,
  router,
}: {
  response: ServiceabilityResponse;
  router: NextRouter;
}) => {
  return (dispatch: Dispatch, getState: () => ReduxState): void => {
    const { monarch } = getState();
    const isMovers = Boolean(monarch?.cartMovers);
    const {
      success = false,
      offerType = "",
      addressHasExistingService = false,
      userErrorMessage = "",
      tipData = null,
      redirect,
      address,
      voiceEligible,
      techAvailable,
      contractType,
      isFutureFiberEligible,
    } = response;

    // Handle basic data and cookies
    setCookie("voiceEligible", Boolean(voiceEligible), { otGroup: "C0003" });
    setCookie("isFutureFiberEligible", Boolean(isFutureFiberEligible), { otGroup: "C0003" });
    if (voiceEligible) dispatch(setVoiceEligible(voiceEligible));
    if (techAvailable) dispatch(setTechAvailable(techAvailable));

    dispatch(setIsFutureFiberEligible(isFutureFiberEligible));

    handleDMAData(dispatch, response);
    if (tipData) {
      setCookie("tipDataFromZipCode", JSON.stringify(tipData), { otGroup: "C0004" });
    }

    if (redirect?.url) {
      dispatch(setUnservRedirect(redirect));
      if (redirect?.permaleaseNumber) {
        dispatch(setCallInNumber(formatPhone(redirect.permaleaseNumber)));
      }

      if (redirect?.provider) {
        setCookie("unservRedirectProvider", redirect.provider, { otGroup: "C0004" });
        return;
      } else if (redirect?.isExternal) {
        // Use safeRedirect to validate the redirect URL before navigating
        safeRedirect(redirect.url);
      } else {
        router.push(redirect.url);
      }
    } else if (success) {
      if (address) {
        dispatch(updateAddress("install", address));

        // Check if address is in AG states (NV, OH, MN, PA) and set cookie
        const AGStates = ["NV", "OH", "MN", "PA"];
        const isAGState = address.state && AGStates.includes(address.state);
        if (isAGState) {
          setCookie("isAGState", "true");
        } else {
          Cookie.remove("isAGState");
        }
      }
      if (offerType) {
        dispatch(setOfferType(offerType));
      }
      if (contractType) {
        console.log("contractType at serviceability", contractType);
        dispatch(setContractType(contractType));
      }
      if (addressHasExistingService || isMovers) {
        dispatch(
          setEligibility({
            isMovers: !addressHasExistingService && isMovers,
            addressHasExistingService,
            serviceable: !addressHasExistingService,
          })
        );
      } else {
        getProducts({
          router,
          isCopper2Fiber: false,
          cartPage: "",
        })(dispatch, getState);
      }
    } else {
      // Handle error case
      dispatch(setPageLoading(false));
      dispatch(
        setNotifications([
          {
            type: "error",
            message:
              userErrorMessage ||
              "Sorry, we're having some technical difficulties. Call ##phoneNumber## and one of our sales experts will help you complete your order.",
          },
        ])
      );
      setNewRelicCustomAttribute([{ name: "serviceabilityError", value: "true" }]);
    }
  };
};
